import { useCallback } from "react";
import {
  arrayUnion,
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  type Firestore,
  type UpdateData,
} from "firebase/firestore";
import type { BaseItem, Item } from "../App";
import { validateCompletionChoice as impactValidateCompletionChoice } from "../lib/impact";
import { computeBillingSteps } from "../lib/billing";
import { computeStateSort } from "../lib/inventoryHelpers";

type CompletionDeps = {
  db: Firestore;
  editId: string | null;
  editForm: Item & { reuse?: boolean; resold?: boolean; scrap?: boolean };
  currentUserString: () => string | null;
  toMillis: (v: any) => number | null;
  LOCK_STALE_MS: number;
  refreshHomeList: () => Promise<void>;
  setEditForm: (updater: (prev: any) => any) => void;
  setIsEditOpen: (v: boolean) => void;
  setEditId: (v: string | null) => void;
};

type ReopenDeps = {
  db: Firestore;
  editId: string | null;
  currentUserString: () => string | null;
  toMillis: (v: any) => number | null;
  LOCK_STALE_MS: number;
  refreshHomeList: () => Promise<void>;
  setEditForm: (updater: (prev: any) => any) => void;
};

export function useEditCompletion({
  db,
  editId,
  editForm,
  currentUserString,
  toMillis,
  LOCK_STALE_MS,
  refreshHomeList,
  setEditForm,
  setIsEditOpen,
  setEditId,
}: CompletionDeps) {
  const markAsCompleted = useCallback(async (): Promise<void> => {
    if (editForm.deletePending) {
      alert("Denna enhet är markerad för radering och kan inte ändras förrän raderingen avmarkeras i listan.");
      return;
    }
    if (!editId) return;

    const err = impactValidateCompletionChoice(editForm);
    if (err && "error" in err) {
      alert(err);
      return;
    }

    if (editForm.resold) {
      const missing =
        !editForm.salesBatchId ||
        !editForm.salesKolliId ||
        !editForm.salesCpu?.trim() ||
        !editForm.salesStorage?.trim() ||
        !editForm.salesRam?.trim();
      if (missing) {
        alert("För vidaresålt krävs försäljningsbatch, kolli och ifyllda fält för CPU, Lagring och Minne.");
        return;
      }
    }

    try {
      const nowIso = new Date().toISOString();
      const user = currentUserString();

      const ref = doc(db, "itInventory", editId);

      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error("Posten finns inte längre.");
        const cur = snap.data() as any;

        if (cur.invoiceReportId) throw new Error("Posten är redan låst för fakturering.");
        if (cur.completed === true) throw new Error("Posten är redan markerad som färdig.");
        if (cur.deletePending === true) throw new Error("Posten är markerad för radering och kan inte färdigmarkeras.");

        const heldByOther = !!cur.lockedBy && cur.lockedBy !== user;
        const lockedAtMs = toMillis(cur.lockedAt);
        const isStale = lockedAtMs === null ? true : (Date.now() - lockedAtMs) > LOCK_STALE_MS;
        if (heldByOther && !isStale) throw new Error(`Posten redigeras av ${cur.lockedBy}.`);

        const selectedCount = Number(cur.reuse === true) + Number(cur.resold === true) + Number(cur.scrap === true);
        if (selectedCount !== 1) throw new Error("Den sparade posten måste ha exakt en vald slutstatus.");
        if (!String(cur.manufacturer || "").trim() || !String(cur.model || "").trim() || !String(cur.serial || "").trim() || !String(cur.grade || "").trim()) {
          throw new Error("Den sparade posten saknar obligatoriska uppgifter.");
        }

        const steps = computeBillingSteps({
          reuse: cur.reuse === true,
          resold: cur.resold === true,
          scrap: cur.scrap === true,
        });

        tx.update(ref, {
          ...steps,
          completed: true,
          completedAt: serverTimestamp(),
          completedBy: user,
          stateSort: computeStateSort(true, cur.invoiceReportId),
          lockedBy: null,
          lockedAt: null,
          updatedAt: serverTimestamp(),
          auditLog: arrayUnion({
            action: "completed",
            by: user,
            at: nowIso,
          }),
        } as UpdateData<BaseItem>);
      });

      setEditForm((prev: any) => ({
        ...prev,
        completed: true,
        completedAt: nowIso,
        completedBy: user,
        stateSort: computeStateSort(true, (prev as any)?.invoiceReportId),
        auditLog: [
          ...(Array.isArray(prev.auditLog) ? prev.auditLog : []),
          { action: "completed", by: user, at: nowIso },
        ],
      }));

      // setIsEditOpen(false); // Commented out to keep modal open after completion
      // setEditId(null); // Commented out to keep modal open after completion
      await refreshHomeList();
    } catch (err: any) {
      alert("Kunde inte markera som färdig: " + (err?.message ?? err));
    }
  }, [
    currentUserString,
    db,
    editForm,
    editId,
    refreshHomeList,
    setEditForm,
    setEditId,
    setIsEditOpen,
    toMillis,
    LOCK_STALE_MS,
  ]);

  return { markAsCompleted };
}

export function useEditReopen({
  db,
  editId,
  currentUserString,
  toMillis,
  LOCK_STALE_MS,
  refreshHomeList,
  setEditForm,
}: ReopenDeps) {
  const reopenForEditing = useCallback(async (): Promise<void> => {
    if (!editId) return;
    try {
      const ref = doc(db, "itInventory", editId);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        alert("Kunde inte låsa upp “ posten finns inte längre.");
        return;
      }
      const data = snap.data() as any;
      if (data.invoiceReportId) {
        alert("Kan inte öppna för editering “ posten är redan fakturerad och är permalåst.");
        return;
      }

      const nowIso = new Date().toISOString();

      await runTransaction(db, async (tx) => {
        const snap2 = await tx.get(ref);
        if (!snap2.exists()) throw new Error("Posten finns inte längre.");

        const cur = snap2.data() as any;
        const user = currentUserString();

        if (cur.invoiceReportId) throw new Error("Kan inte öppna för editering “ posten är fakturerad och permalåst.");

        const heldByOther = !!cur.lockedBy && cur.lockedBy !== user;
        const lockedAtMs = toMillis(cur.lockedAt);
        const isStale = lockedAtMs === null ? true : (Date.now() - lockedAtMs) > LOCK_STALE_MS;
        if (heldByOther && !isStale) {
          throw new Error(`Posten redigeras av ${cur.lockedBy}.`);
        }

        tx.update(ref, {
          completed: false,
          completedAt: null,
          completedBy: null,
          markedForInvoice: false,
          stateSort: computeStateSort(false, null),
          lockedBy: user,
          lockedAt: serverTimestamp(),
          auditLog: arrayUnion({
            action: "reopened",
            by: user,
            at: nowIso,
          }),
        } as UpdateData<BaseItem>);
      });

      setEditForm((prev: any) => ({
        ...prev,
        completed: false,
        completedAt: null,
        completedBy: null,
        stateSort: computeStateSort(false, null),
        auditLog: [
          ...(Array.isArray(prev.auditLog) ? prev.auditLog : []),
          { action: "reopened", by: currentUserString(), at: nowIso },
        ],
      }));

      await refreshHomeList();
    } catch (err: any) {
      alert("Kunde inte låsa upp för editering: " + err.message);
    }
  }, [
    LOCK_STALE_MS,
    currentUserString,
    db,
    editId,
    refreshHomeList,
    setEditForm,
    toMillis,
  ]);

  return { reopenForEditing };
}
